// Source / artifact derivation for the global Layer Properties console.
//
// Layer-KIND + endpoint aware classification of the underlying source artifact
// (COG, PMTiles, 3D Tiles, point cloud, terrain, …) plus the metadata details
// the Source tab shows when available. Safe with missing metadata: every reader
// degrades to `null` / empty.

import type { Layer } from '@geosoft/viewer-sdk';
import { isCopcEndpoint, isPotreeMetadataEndpoint } from './renderPath';
import type {
  EndpointRef,
  LayerSourceDiagnostics,
  LiveRenderDiagnostics,
  SourceArtifactClass,
  SourceArtifactSummary,
} from './types';

// ---------------------------------------------------------------------------
// Small structural readers
// ---------------------------------------------------------------------------

export function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function styleRecord(layer: Layer): Record<string, unknown> | null {
  return asRecord(layer.style);
}

/** The converter manifest carried by reference on `style.intakeManifest`. */
export function intakeManifest(layer: Layer): Record<string, unknown> | null {
  const style = styleRecord(layer);
  return asRecord(style?.['intakeManifest']);
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return null;
}

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function listEndpoints(layer: Layer): EndpointRef[] {
  const out: EndpointRef[] = [];
  const endpoints = layer.endpoints ?? {};
  for (const [key, value] of Object.entries(endpoints)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      out.push({ key, url: value });
    }
  }
  return out;
}

/** Priority of endpoint keys for picking the "primary" source URL per kind. */
const PRIMARY_ENDPOINT_PRIORITY: readonly string[] = [
  'tileset3d',
  'copc',
  'potree',
  'model',
  'pmtiles',
  'terrain',
  'tiles',
  'features',
  'manifest',
  'stations',
  'stream',
  'pointcloud',
];

export function primaryEndpoint(endpoints: readonly EndpointRef[]): EndpointRef | null {
  for (const key of PRIMARY_ENDPOINT_PRIORITY) {
    const found = endpoints.find((e) => e.key === key);
    if (found) return found;
  }
  return endpoints[0] ?? null;
}

// ---------------------------------------------------------------------------
// Artifact classification
// ---------------------------------------------------------------------------

export function readArtifactKind(layer: Layer): string | null {
  const style = styleRecord(layer);
  return firstString(
    style?.['gtwinArtifactKind'],
    style?.['artifactKind'],
    style?.['intakeKind'],
  );
}

function looksLikeCogTemplate(url: string | undefined): boolean {
  if (!url) return false;
  return /\/cog\/tiles?\//i.test(url) || /[?&]url=.*\.tif/i.test(url);
}

export function classifyArtifact(layer: Layer): SourceArtifactClass {
  const { endpoints } = layer;
  const artifactKind = (readArtifactKind(layer) ?? '').toLowerCase();

  if (layer.kind === 'environment' || layer.id.startsWith('layer:environment:')) {
    return 'environment';
  }
  if (endpoints.tileset3d) return '3dtiles';
  if (endpoints.copc || isCopcEndpoint(endpoints.pointcloud)) return 'pointcloud-copc';
  if (endpoints.potree || isPotreeMetadataEndpoint(endpoints.pointcloud)) {
    return 'pointcloud-potree';
  }
  if (endpoints.model) return 'glb';
  if (layer.kind === 'terrain') {
    return artifactKind.includes('quantized') ? 'quantized-mesh' : 'terrain-cog';
  }
  if (endpoints.pmtiles) return 'pmtiles';
  if (endpoints.stream) return 'sensor-stream';
  if (endpoints.manifest || endpoints.stations) return 'panorama';
  if (endpoints.features) {
    return artifactKind.includes('ogc') ? 'ogc-features' : 'geojson';
  }
  if (endpoints.tiles) {
    return looksLikeCogTemplate(endpoints.tiles) || artifactKind === 'cog' ? 'cog' : 'xyz-raster';
  }
  return 'unknown';
}

const ARTIFACT_DESCRIPTION: Record<SourceArtifactClass, string> = {
  cog: 'Cloud-Optimized GeoTIFF (raster, tiled server-side)',
  pmtiles: 'PMTiles archive (single-file vector/raster tiles)',
  '3dtiles': 'OGC 3D Tiles tileset',
  glb: 'glTF / GLB model',
  'pointcloud-copc': 'Cloud-Optimized Point Cloud (COPC)',
  'pointcloud-potree': 'Potree v2 octree point cloud',
  'terrain-cog': 'Terrain DEM (COG elevation/colour)',
  'quantized-mesh': 'Quantized-mesh terrain tiles',
  geojson: 'GeoJSON features',
  'ogc-features': 'OGC API Features snapshot',
  'xyz-raster': 'XYZ raster tiles',
  environment: 'Environment / scene effect (no spatial source)',
  'sensor-stream': 'Sensor / timeseries stream',
  panorama: 'Panorama imagery + pose',
  unknown: 'Unclassified source',
};

function artifactDetails(
  layer: Layer,
  classified: SourceArtifactClass,
): Array<{ label: string; value: string }> {
  const details: Array<{ label: string; value: string }> = [];
  const manifest = intakeManifest(layer) ?? {};
  const style = styleRecord(layer) ?? {};
  const pc = asRecord(style['pointcloud']) ?? {};

  const push = (label: string, value: string | number | null | undefined) => {
    if (value === null || value === undefined) return;
    const str = typeof value === 'number' ? value.toLocaleString() : value;
    if (str.trim().length > 0) details.push({ label, value: str });
  };

  switch (classified) {
    case 'pmtiles':
      push('Feature count', firstNumber(manifest['featureCount']));
      push('Tile count', firstNumber(manifest['tileCount']));
      break;
    case 'cog':
    case 'xyz-raster':
    case 'terrain-cog':
      push('Manifest CRS', firstString(manifest['crs'], manifest['crsOut']));
      push('Nodata', firstString(style['nodataValue'], style['titilerNodata']));
      break;
    case '3dtiles':
      push('Geometric error', firstNumber(manifest['geometricError']));
      push('LOD count', firstNumber(manifest['lodCount']));
      break;
    case 'pointcloud-copc':
    case 'pointcloud-potree':
      push('Point count', firstNumber(pc['pointCount'], manifest['pointCount']));
      push('Point format (PDRF)', firstNumber(pc['pointFormat']));
      push('LAS version', firstString(pc['lasVersion']));
      push('Safety class', firstString(pc['safetyClass']));
      push('Coordinate mode', firstString(pc['coordinateMode']));
      {
        const dims = pc['dimensions'];
        if (Array.isArray(dims) && dims.length > 0) push('Dimensions', dims.join(', '));
      }
      {
        const modes = pc['colorModes'];
        if (Array.isArray(modes) && modes.length > 0) push('Color modes', modes.join(', '));
      }
      {
        const classifications = pc['classifications'];
        if (Array.isArray(classifications) && classifications.length > 0) {
          push('Classification classes', String(classifications.length));
        }
      }
      push('Density (pts/m\u00B2)', firstNumber(pc['densityEstimate']));
      {
        const budget = asRecord(pc['pointBudget']);
        if (budget) {
          push('Recommended budget', firstNumber(budget['recommendedInteractiveBudget']));
        }
      }
      break;
    default:
      break;
  }
  return details;
}

export function buildSourceArtifactSummary(layer: Layer): SourceArtifactSummary {
  const classified = classifyArtifact(layer);
  return {
    artifactKind: readArtifactKind(layer),
    classified,
    description: ARTIFACT_DESCRIPTION[classified],
    details: artifactDetails(layer, classified),
  };
}

// ---------------------------------------------------------------------------
// Source-layer + size + count
// ---------------------------------------------------------------------------

export function resolveSelectedSourceLayer(
  layer: Layer,
  live?: LiveRenderDiagnostics | null,
): string | null {
  if (layer.kind !== 'vector') return null;
  const style = styleRecord(layer);
  // Prefer what the engine ACTUALLY bound (post-reconciliation) over the
  // declared style value, which may be a stale name the archive lacks.
  return firstString(
    live?.selectedSourceLayer ?? undefined,
    style?.['sourceLayer'],
    style?.['source_layer'],
  );
}

export function resolveDeclaredSourceLayer(
  layer: Layer,
  live?: LiveRenderDiagnostics | null,
): string | null {
  if (layer.kind !== 'vector') return null;
  const style = styleRecord(layer);
  return firstString(
    style?.['sourceLayer'],
    style?.['source_layer'],
    live?.declaredSourceLayer ?? undefined,
  );
}

function formatSize(bytes: number | null, megabytes: number | null): string | null {
  if (bytes !== null) {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes.toFixed(0)} B`;
  }
  return megabytes !== null ? `${megabytes.toFixed(2)} MB` : null;
}

export function estimateSize(layer: Layer): string | null {
  const manifest = intakeManifest(layer);
  const style = styleRecord(layer);
  return formatSize(
    firstNumber(
      manifest?.['archiveSizeBytes'],
      manifest?.['sizeBytes'],
      style?.['byteSize'],
      style?.['bytes'],
    ),
    firstNumber(style?.['sizeMb'], style?.['totalMb']),
  );
}

export function estimateCount(layer: Layer): string | null {
  const manifest = intakeManifest(layer);
  const style = styleRecord(layer);
  const pc = asRecord(style?.['pointcloud']);
  const count = firstNumber(
    manifest?.['featureCount'],
    manifest?.['pointCount'],
    manifest?.['tileCount'],
    pc?.['pointCount'],
    style?.['featureCount'],
    style?.['objectCount'],
  );
  return count !== null ? count.toLocaleString() : null;
}

export function buildSourceDiagnostics(
  layer: Layer,
  live?: LiveRenderDiagnostics | null,
): LayerSourceDiagnostics {
  const endpoints = listEndpoints(layer);
  const available =
    layer.kind === 'vector' && Array.isArray(live?.availableSourceLayers)
      ? [...(live!.availableSourceLayers as readonly string[])]
      : null;
  return {
    endpoints,
    primaryEndpoint: primaryEndpoint(endpoints),
    artifact: buildSourceArtifactSummary(layer),
    selectedPmtilesSourceLayer: resolveSelectedSourceLayer(layer, live),
    declaredPmtilesSourceLayer: resolveDeclaredSourceLayer(layer, live),
    availablePmtilesSourceLayers: available && available.length > 0 ? available : null,
    pmtilesArchive: live?.pmtilesArchive ?? null,
    estimatedSize: estimateSize(layer),
    featureOrObjectCount: estimateCount(layer),
  };
}
